import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * 404 catch-all page.
 * Rendered by App.js's <Route path="*"> at the end of the route table for
 * any URL that doesn't match a defined route (e.g. /{lang}/compare/X-vs-Y,
 * /{lang}/faq/{slug}, /{lang}/products/{slug}).
 *
 * Critical SEO behavior (GSC iter-45 follow-up, fixes ~11 soft 404s
 * reported under "Alternate page with proper canonical tag"):
 *   - Emits <meta name="robots" content="noindex, follow"> via Helmet.
 *   - Removes any pre-injected self-referencing canonical so Google does
 *     not get a conflicting "index me" signal from the IIFE in index.html.
 *   - Tells Googlebot we know this URL is invalid and to drop it from the
 *     index. Because we're an SPA we cannot return a hard 404 HTTP status;
 *     the noindex tag is the standards-compliant equivalent.
 *
 * Bilingual copy uses the active language from LanguageContext so the
 * page still feels native to the visitor's locale.
 */
const COPY = {
  en: { h1: 'Page not found', body: "Sorry, we couldn't find the page you were looking for. It may have moved or never existed.", products: 'Browse our research peptides', home: 'Back to homepage' },
  es: { h1: 'Página no encontrada', body: 'Lo sentimos, no pudimos encontrar la página que buscaba. Puede que se haya movido o que nunca existiera.', products: 'Ver nuestros péptidos de investigación', home: 'Volver al inicio' },
  ru: { h1: 'Страница не найдена', body: 'К сожалению, мы не смогли найти запрошенную страницу. Возможно, она была перемещена или никогда не существовала.', products: 'Посмотреть наши пептиды', home: 'Вернуться на главную' },
  de: { h1: 'Seite nicht gefunden', body: 'Leider konnten wir die gesuchte Seite nicht finden. Sie wurde möglicherweise verschoben oder hat nie existiert.', products: 'Forschungspeptide ansehen', home: 'Zur Startseite' },
  fr: { h1: 'Page introuvable', body: 'Désolé, nous n\'avons pas pu trouver la page que vous recherchez. Elle a peut-être été déplacée ou n\'a jamais existé.', products: 'Voir nos peptides de recherche', home: 'Retour à l\'accueil' },
  it: { h1: 'Pagina non trovata', body: 'Spiacenti, non abbiamo trovato la pagina che stavi cercando. Potrebbe essere stata spostata o non essere mai esistita.', products: 'Sfoglia i peptidi di ricerca', home: 'Torna alla home' },
  pl: { h1: 'Nie znaleziono strony', body: 'Przepraszamy, nie mogliśmy znaleźć szukanej strony. Mogła zostać przeniesiona lub nigdy nie istniała.', products: 'Przeglądaj peptydy badawcze', home: 'Wróć na stronę główną' },
  nl: { h1: 'Pagina niet gevonden', body: 'Sorry, we konden de pagina die u zocht niet vinden. Mogelijk is deze verplaatst of heeft deze nooit bestaan.', products: 'Bekijk onze onderzoekspeptiden', home: 'Terug naar de homepage' },
  pt: { h1: 'Página não encontrada', body: 'Desculpe, não foi possível encontrar a página que procurava. Pode ter sido movida ou nunca ter existido.', products: 'Ver os peptídeos de pesquisa', home: 'Voltar à página inicial' },
};

const NotFoundPage = () => {
  const { currentLanguage } = useLanguage();
  const t = COPY[currentLanguage] || COPY.en;
  const langPrefix = `/${currentLanguage || 'en'}`;

  // Imperative cleanup: remove the self-referencing canonical injected by
  // the IIFE in index.html so Google doesn't receive "noindex" AND
  // "canonical=self" simultaneously (which it would interpret as an
  // indexable page with a noindex hint, leading to mixed signals).
  // Also rewrite the static index.html robots/googlebot meta tags from
  // "index, follow, ..." to "noindex, follow", Helmet APPENDS its own
  // <meta name="robots"> rather than replacing the static one, so without
  // this imperative override Googlebot would see two conflicting robots
  // directives on the same page.
  useEffect(() => {
    const ORIGINAL_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1';
    const ORIGINAL_GOOGLEBOT = 'index, follow';
    try {
      const canon = document.querySelector('link[rel="canonical"]');
      if (canon) canon.parentNode.removeChild(canon);
      const alts = document.querySelectorAll('link[rel="alternate"][hreflang]');
      alts.forEach((el) => el.parentNode.removeChild(el));
      document.querySelectorAll('meta[name="robots"]').forEach((el) => {
        el.setAttribute('content', 'noindex, follow');
      });
      document.querySelectorAll('meta[name="googlebot"]').forEach((el) => {
        el.setAttribute('content', 'noindex, follow');
      });
    } catch (_) { /* never block render */ }

    // Restore on unmount so subsequent SPA navigations (e.g., clicking the
    // "Browse products" CTA) do not inherit the noindex directive.
    return () => {
      try {
        document.querySelectorAll('meta[name="robots"]').forEach((el) => {
          el.setAttribute('content', ORIGINAL_ROBOTS);
        });
        document.querySelectorAll('meta[name="googlebot"]').forEach((el) => {
          el.setAttribute('content', ORIGINAL_GOOGLEBOT);
        });
      } catch (_) { /* never block unmount */ }
    };
  }, []);

  return (
    <div data-testid="not-found-page" className="min-h-screen flex items-center justify-center bg-white px-6 py-24">
      <Helmet>
        <title>{t.h1}, Penlab Peptide</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="googlebot" content="noindex, follow" />
      </Helmet>
      <div className="max-w-xl text-center">
        <p className="text-sm font-medium uppercase tracking-widest text-teal-600">404</p>
        <h1 data-testid="not-found-heading" className="mt-4 text-4xl sm:text-5xl font-semibold text-slate-900">{t.h1}</h1>
        <p className="mt-6 text-base text-slate-600">{t.body}</p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to={`${langPrefix}/products`}
            data-testid="not-found-products-link"
            className="inline-flex items-center justify-center rounded-full bg-teal-600 px-6 py-3 text-sm font-medium text-white hover:bg-teal-700 transition-colors"
          >
            {t.products}
          </Link>
          <Link
            to={langPrefix}
            data-testid="not-found-home-link"
            className="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            {t.home}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
